import React, { useState } from 'react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { calculateEventHeight, timeToPixels } from '../../../utils/calendar/timeCalculations';
import { getEventType, getEventColors } from '../../../utils/calendar/calendarHelpers';
import { formatServiceInfo } from '../../../utils/calendar/durationService';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const TrainingEvent = React.memo(({
                                      event,
                                      dayStart,
                                      hourHeight,
                                      columnWidth,
                                      columnOffset = 0,
                                      totalColumns = 1,
                                      onEdit,
                                      onDelete,
                                      onClick,
                                      onDragStart,
                                      onResizeStart,
                                      isDraggable = true,
                                      isResizable = true,
                                      onEditTemporaryLock,
                                      onDeleteTemporaryLock,
                                  }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Calculate position and dimensions
    const startTime = dayjs(event.startTime);
    const endTime = dayjs(event.endTime);
    const top = timeToPixels(startTime, dayStart, hourHeight);
    const height = calculateEventHeight(startTime, endTime, hourHeight);

    // ADD VERTICAL CENTERING OFFSET
    const verticalOffset = 4; // Small offset to center between grid lines
    const topMargin = 2;
    const bottomMargin = 2;
    const heightWithMargins = Math.max(height - 9, 20); // Only 4px total margins
    const topWithMargin = top + verticalOffset; // Add offset here

    // Determine event type and get appropriate class
    const eventType = getEventType(event);
    const eventClass = getEventClass(eventType, event);

    // Calculate width based on overlapping columns
    const width = totalColumns > 1
        ? `calc(${100 / totalColumns}% - 20px)`
        : 'calc(100% - 20px)';

    const left = totalColumns > 1
        ? `calc(${columnOffset * (100 / totalColumns)}% + 10px)`
        : '10px';

    // Handle event click with propagation stop
    const handleEventClick = (e) => {
        e.stopPropagation();

        // Special handling for temporary locks - open edit modal
        if (event.isTemporary && onEditTemporaryLock) {
            onEditTemporaryLock();
            return;
        }

        // Original click logic for other events
        onClick?.(event);
    };

    // Handle mouse down with propagation stop
    const handleMouseDown = (e) => {
        // Stop propagation to prevent parent's mousedown handler
        e.stopPropagation();
    };

    // Handle resize start - disabled for temporary locks
    const handleResizeStart = (e, edge) => {
        if (!isResizable || event.isTemporary) return;
        e.stopPropagation();
        e.preventDefault();
        onResizeStart?.(e, event, edge);
    };

    // Handle drag start - disabled for temporary locks
    const handleDragStart = (e) => {
        if (!isDraggable || event.isTemporary) return;
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.(e, event);
    };

    // Handle edit button click
    const handleEditClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onEdit(event);
    };

    // Handle delete button click
    const handleDeleteClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onDelete(event);
    };

    // Handle temporary lock delete
    const handleDeleteTemporaryLock = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onDeleteTemporaryLock?.();
    };

    // Get event content based on type
    const renderEventContent = () => {
        // Calculate duration in minutes
        const durationMinutes = endTime.diff(startTime, 'minute');
        const isShortEvent = durationMinutes < 60; // Less than 1 hour

        // Detect mobile/tablet for additional spacing considerations
        const isMobileOrTablet = window.innerWidth <= 1024;
        const isMobile = window.innerWidth <= 768;

        // More aggressive height thresholds for mobile
        const mobileMinHeight = isMobile ? 35 : 40;
        const tabletMinHeight = isMobileOrTablet ? 38 : 40;
        const minHeightForTime = isMobile ? mobileMinHeight : tabletMinHeight;

        // Check for pending bookings first (they have bookingCount)
        if (event.bookingCount && event.bookingCount > 0) {
            // If sharing column space, show minimal or no text
            if (totalColumns > 1) {
                return (
                    <div className="event-title" style={{ fontSize: isMobile ? '10px' : '12px', textAlign: 'center' }}>
                    </div>
                );
            }
            // Full width - show complete text
            return (
                <>
                    <div className="event-title">Oczekujące</div>
                    <div className="event-subtitle">Rezerwacje: {event.bookingCount}</div>
                </>
            );
        } else if (event.confirmed && event.dogDTO) {
            // UPDATED: Always hide time for short events (< 1 hour), also hide for small heights on mobile
            const shouldHideTime = isShortEvent || (isMobileOrTablet && height < minHeightForTime);
            return (
                <>
                    <div className="event-title" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                        {event.dogDTO.name}
                    </div>
                    {!isShortEvent && !shouldHideTime && (
                        <div className="event-time" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                            {startTime.format('HH:mm')}
                        </div>
                    )}
                </>
            );
        } else if (event.trainingGroup) {
            // UPDATED: Always hide time for short events (< 1 hour), also hide for small heights on mobile
            const shouldHideTime = isShortEvent || (isMobileOrTablet && height < minHeightForTime);

            // If sharing column space, show minimal info
            if (totalColumns > 1) {
                return (
                    <>
                        <div className="event-title" style={{ fontSize: isMobile ? '11px' : '13px' }}>
                            {event.trainingGroup.name}
                        </div>
                        {!isShortEvent && height > minHeightForTime && !shouldHideTime && (
                            <div className="event-time" style={{ fontSize: isMobile ? '9px' : '11px' }}>
                                {startTime.format('HH:mm')}
                            </div>
                        )}
                    </>
                );
            }
            // Full width - show complete info
            return (
                <>
                    <div className="event-title" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                        {event.trainingGroup.name}
                    </div>
                    {!isShortEvent && !shouldHideTime && (
                        <div className="event-time" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                            {startTime.format('HH:mm')}
                        </div>
                    )}
                    <div className="event-subtitle" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                        Grupa: {event.trainingGroup.dogsCount} psów
                    </div>
                    {event.hallName && (
                        <div className="event-subtitle" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                            Sala: {event.hallName}
                        </div>
                    )}
                </>
            );
        } else if (event.isLocked) {
            // UPDATED: Always hide time for short events (< 1 hour), also hide for small heights on mobile
            const shouldHideTime = isShortEvent || (isMobileOrTablet && height < minHeightForTime);

            // If sharing column space and not temporary, show minimal info
            if (totalColumns > 1 && !event.isTemporary) {
                return (
                    <div className="event-title" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                        {height > (isMobile ? 25 : 30) ? 'Zablok.' : '🔒'}
                    </div>
                );
            }
            // Full width or temporary - show complete info
            return (
                <>
                    <div className="event-title" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                        {event.isTemporary ? 'Nowa blokada' : 'Zablokowane'}
                    </div>
                    {!isShortEvent && height > minHeightForTime && !shouldHideTime && (
                        <div className="event-subtitle" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                            {startTime.format('HH:mm')} - {endTime.format('HH:mm')}
                        </div>
                    )}
                    {event.isTemporary && height > (isMobile ? 50 : 60) && (
                        <div className="event-subtitle" style={{
                            fontSize: isMobile ? '9px' : '11px',
                            marginTop: '4px'
                        }}>
                            Kliknij aby edytować
                        </div>
                    )}
                </>
            );
        } else {
            return (
                <div className="event-title" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    Wolny termin
                </div>
            );
        }
    };

    // UPDATED: Render action buttons with consistent styling for both lock types
    const renderActions = () => {
        if (!isHovered) return null;

        // FIXED: Both temporary AND permanent locks should show delete button in same style
        if ((event.isTemporary && onDeleteTemporaryLock) || (event.isLocked && !event.isTemporary && onDelete)) {
            const handleDelete = event.isTemporary ? onDeleteTemporaryLock : () => onDelete(event);

            return (
                <div
                    className="event-actions"
                    style={{
                        position: 'absolute',
                        top: '25%', // Moved even higher from 35% to 25%
                        right: '8px',
                        transform: 'translateY(-50%)', // Keep the transform for precise positioning
                        display: 'flex',
                        gap: '4px',
                        opacity: 1, // Always visible for both lock types
                        zIndex: 20,
                    }}
                >
                    <button
                        className="action-btn"
                        onClick={handleDelete}
                        onMouseDown={(e) => e.stopPropagation()}
                        title="Usuń"
                        style={{
                            background: 'rgba(255, 255, 255, 0.3)',
                            border: 'none',
                            borderRadius: '50%', // Circular for both
                            padding: '0',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px', // Same size for both
                            height: '24px',
                            fontWeight: 'bold',
                        }}
                    >
                        ✕
                    </button>
                </div>
            );
        }

        // Don't show actions for confirmed trainings - they'll be in the modal
        if (event.confirmed) return null;

        const showEdit = event.trainingGroup && onEdit;

        if (!showEdit) return null;

        return (
            <div className="event-actions">
                <button
                    className="action-btn"
                    onClick={handleEditClick}
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Edytuj"
                >
                    ✎
                </button>
            </div>
        );
    };

    return (
        <div
            className={`event-block ${eventClass}`}
            style={{
                position: 'absolute',
                top: `${topWithMargin}px`, // Using the offset top position
                left,
                width,
                height: `${heightWithMargins}px`,
                cursor: event.isTemporary ? 'pointer' : (isDraggable ? 'move' : 'pointer'),
                opacity: event.isTemporary ? 0.8 : 1,
                border: event.isTemporary ? '2px solid #3b82f6' : undefined,
            }}
            onClick={handleEventClick}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            draggable={isDraggable && !event.isTemporary}
            onDragStart={handleDragStart}
        >
            {/* Resize handles - disabled for temporary locks */}
            {isResizable && isHovered && !event.isTemporary && (
                <>
                    <div
                        className="event-resize-handle event-resize-top"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '8px',
                            cursor: 'ns-resize',
                            background: 'rgba(0, 0, 0, 0.1)',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                        onMouseDown={(e) => handleResizeStart(e, 'top')}
                    />
                    <div
                        className="event-resize-handle event-resize-bottom"
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '8px',
                            cursor: 'ns-resize',
                            background: 'rgba(0, 0, 0, 0.1)',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                        onMouseDown={(e) => handleResizeStart(e, 'bottom')}
                    />
                </>
            )}

            {/* Event content */}
            {renderEventContent()}

            {/* Action buttons */}
            {renderActions()}
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function
    return (
        prevProps.event.id === nextProps.event.id &&
        prevProps.event.startTime === nextProps.event.startTime &&
        prevProps.event.endTime === nextProps.event.endTime &&
        prevProps.event.isTemporary === nextProps.event.isTemporary &&
        prevProps.columnOffset === nextProps.columnOffset &&
        prevProps.totalColumns === nextProps.totalColumns &&
        prevProps.hourHeight === nextProps.hourHeight &&
        prevProps.isDraggable === nextProps.isDraggable &&
        prevProps.isResizable === nextProps.isResizable &&
        dayjs(prevProps.dayStart).isSame(dayjs(nextProps.dayStart))
    );
});

TrainingEvent.displayName = 'TrainingEvent';

// Helper function to get event class based on type
const getEventClass = (eventType, event) => {
    // Special handling for pending bookings
    if (event.bookingCount && event.bookingCount > 0) {
        return 'event-pending';
    }

    // Special handling for temporary locks
    if (event.isTemporary) {
        return 'event-temporary-lock';
    }

    const classMap = {
        confirmed: 'event-confirmed',
        pending: 'event-pending',
        group: 'event-confirmed-other',
        locked: 'event-locked',
        available: 'event-free',
    };

    return classMap[eventType] || 'event-free';
};

export default TrainingEvent;